/**
 * Legend Label Constants
 * 
 * This file contains all label mappings and classifications for the climate risk legend component.
 * Centralized constants improve performance by avoiding object recreation and provide a single source of truth.
 */

import { ScenarioSSP, TimePeriod } from '../../../model/dataset/rendering/climatology-options';
import { FREQUENCY_INDICATORS } from '../../../shared/colorize-markers/climate-indicators.constants';

/**
 * Scenario SSP label mappings
 * Maps ScenarioSSP enum values to their formatted display labels
 */
export const SCENARIO_LABELS: Record<ScenarioSSP, string> = {
    [ScenarioSSP.SSP119]: 'SSP1-1.9',
    [ScenarioSSP.SSP126]: 'SSP1-2.6',
    [ScenarioSSP.SSP245]: 'SSP2-4.5',
    [ScenarioSSP.SSP370]: 'SSP3-7.0',
    [ScenarioSSP.SSP585]: 'SSP5-8.5'
};

/**
 * Time period label mappings
 * Maps TimePeriod enum values to their term display labels with year ranges
 */
export const TIME_PERIOD_LABELS: Record<TimePeriod, string> = {
    [TimePeriod.NEAR]: 'Near Term (2021-2040)',
    [TimePeriod.MID]: 'Mid Term (2041-2060)',
    [TimePeriod.LONG]: 'Long Term (2081-2100)',
    [TimePeriod.HISTORIC]: 'Historical (1995–2014)',
};

/**
 * Acute hazard indicators (Probability Scale)
 * 
 * These indicators represent events with probability-based risk assessment.
 * Based on Veolia's climate risk classification.
 * 
 * Acute hazards include:
 * - Frost days (FD)
 * - Maximum consecutive dry days (CDD) - Wildfire risk
 * - Maximum 1-day accumulated precipitation (RX1day) - Flood risk
 * - Maximum 5-day accumulated precipitation (RX5day) - Flood/Landslide risk
 * - Days with temperature above 35°C (TX35) - Heat wave
 * - Days with temperature above 40°C (TX40) - Extreme heat wave
 * - Standardised Precipitation Index (SPI-6) - Drought/Water stress
 */
export const ACUTE_INDICATORS: ReadonlyArray<string> = [
    'climate_fd',      // Frost days (FD)
    'climate_cdd',     // Maximum consecutive dry days (CDD) - Wildfire
    'climate_rx1day',  // Maximum of 1-day accumulated precipitation (RX1day)
    'climate_rx5day',  // Maximum of 5-day accumulated precipitation (RX5day)
    'climate_tx35',    // Number of days with maximum temperature above 35°C (TX35)
    'climate_tx40',    // Number of days with maximum temperature above 40°C (TX40)
    'climate_spi6'     // Standardised Precipitation Index (SPI-6)
];

/**
 * Acute hazard score labels (Probability Scale)
 * Used for indicators in ACUTE_INDICATORS list
 */
export const ACUTE_SCORE_LABELS: Record<number, string> = {
    1: 'Very Low Probability',
    2: 'Low Probability',
    3: 'Medium Probability',
    4: 'High Probability',
    5: 'Very High Probability'
};

/**
 * Frequency-based score labels (Frequency Scale)
 * Used for indicators in FREQUENCY_INDICATORS list (imported from climate-indicators.constants.ts)
 */
export const FREQUENCY_SCORE_LABELS: Record<number, string> = {
    1: 'Very Low Frequency',
    2: 'Low Frequency',
    3: 'Medium Frequency',
    4: 'High Frequency',
    5: 'Very High Frequency'
};

/**
 * Chronic hazard score labels (Intensity Scale)
 * 
 * Used for all indicators NOT in ACUTE_INDICATORS or FREQUENCY_INDICATORS lists.
 * 
 * Chronic hazards include:
 * - Maximum of daily maximum temperature (TXx)
 * - Mean of daily accumulated precipitation (PR)
 * - Mean of daily mean temperature (T)
 * - Mean of daily accumulated snowfall (PRSN)
 * - Mean of daily mean wind speed (SFCWIND)
 * - Mean Sea Surface Temperature (SST)
 * - Sea level rise (SLR)
 */
export const CHRONIC_SCORE_LABELS: Record<number, string> = {
    1: 'Very Low Intensity',
    2: 'Low Intensity',
    3: 'Medium Intensity',
    4: 'High Intensity',
    5: 'Very High Intensity'
};

/**
 * Check if an indicator is an acute hazard (probability-based)
 * @param indicator - The climate indicator code (e.g., 'climate_fd', 'climate_cd')
 * @returns true if acute (probability scale), false if chronic (intensity scale)
 */
export function isAcuteIndicator(indicator: string | undefined): boolean {
    if (!indicator) {
        return true; // Default to acute
    }
    return ACUTE_INDICATORS.includes(indicator.toLowerCase());
}

/**
 * Check if an indicator is frequency-based
 * @param indicator - The climate indicator code (e.g., 'climate_cd_v2', 'climate_hd_v2')
 * @returns true if frequency-based, false otherwise
 */
export function isFrequencyIndicator(indicator: string | undefined): boolean {
    if (!indicator) {
        return false;
    }
    return FREQUENCY_INDICATORS.includes(indicator.toLowerCase());
}

/**
 * Get the appropriate score label based on indicator type
 * @param score - Score value (1-5)
 * @param indicator - The climate indicator code
 * @returns Formatted score label (e.g., "Very high probability", "Very high frequency", or "Very high intensity")
 */
export function getScoreLabelForIndicator(score: number, indicator: string | undefined): string {
    // Check frequency first (takes precedence over acute for overlapping indicators like FD, TX35, TX40)
    if (isFrequencyIndicator(indicator)) {
        return FREQUENCY_SCORE_LABELS[score] || '';
    }
    
    // Then check acute
    if (isAcuteIndicator(indicator)) {
        return ACUTE_SCORE_LABELS[score] || '';
    }
    
    // Default to chronic/intensity
    return CHRONIC_SCORE_LABELS[score] || '';
}
